import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import TeamFormBody from "./TeamFormBody";
import {
  useCreateTeamMutation,
  useEditTeamMutation,
  useGetSpecificTeamQuery,
  useDeleteTeamAttachmentsMutation,
} from "../../../Redux/service/Teams/Teams";
import { Formik, Form as FormFormik } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import { useContext, useEffect } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";

export default function FormTeam({ teamForm, setTeamForm, isEditing }) {
  const { setEdited, attachmentsId, setAttachmentsId, handleClose } =
    useContext(popupcontext);
  const { refetch, data: editData } = useGetSpecificTeamQuery(teamForm, {
    skip: !isEditing || !teamForm,
  });
  const [createTeam] = useCreateTeamMutation();
  const [editTeam] = useEditTeamMutation();
  const [deleteAttachment] = useDeleteTeamAttachmentsMutation();

  useEffect(() => {
    if (isEditing && teamForm) {
      refetch();
    }
  }, [isEditing, teamForm, refetch]);

  let initialValues = {};
  if (isEditing && editData?.data) {
    const data = editData?.data;
    initialValues = {
      code: data?.code || "",
      name: data?.name || "",
      companyProjectId: data?.companyProjectId || "",
      trades: data?.trades?.map((trade) => trade.tradeId) || [],
      assignedZones: data?.assignedZones?.map((zone) => zone.id) || [],
      role: data?.role || "",
      memberUserIds: data?.members?.map((user) => Number(user.id)) || [],
      attachments: null,
    };
    console.log("initialValues", initialValues);
  } else {
    initialValues = {
      code: "",
      name: "",
      companyProjectId: "",
      trades: [],
      assignedZones: [],
      role: "",
      memberUserIds: [],
      attachments: null,
    };
  }

  const validationSchema = Yup.object({
    code: Yup.string()
      .required(trans("Team code is required"))
      .matches(/^[^\s]/, trans("cannot start with a space")),
    name: Yup.string()
      .required(trans("Team name is required"))
      .matches(/^[^\s]/, trans("cannot start with a space")),
    companyProjectId: Yup.number()
      .required(trans("Company Project is required"))
      .positive(trans("Company Project must be selected")),
    role: Yup.string().required(trans("Role is required")),
  });

  const handleFormSubmit = async (values, setSubmitting, resetForm) => {
    setSubmitting(true);

    const payload = {
      ...values,
      trades: `[${
        Array.isArray(values.trades) ? values.trades.join(",") : values.trades
      }]`,
      assignedZones: `[${
        Array.isArray(values.assignedZones)
          ? values.assignedZones.join(",")
          : values.assignedZones
      }]`,
    };

    try {
      if (!isEditing && teamForm) {
        await createTeam(payload).unwrap();
        toast.success(trans("Team created successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else {
        await editTeam({
          id: teamForm,
          payload: payload,
        }).unwrap();

        // Delete attachments that were marked for deletion
        if (attachmentsId && attachmentsId.length > 0) {
          try {
            await Promise.all(
              attachmentsId.map(async (id) => {
                await deleteAttachment(id).unwrap();
              }),
            );
          } catch (error) {
            console.error("Error deleting attachments:", error);
            // Continue even if attachment deletion fails
          }
          // Clear attachmentsId after deletion
          setAttachmentsId([]);
        }

        toast.success(trans("Team edited successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }

      resetForm();
      setTeamForm(false);
      setEdited(true);
      handleClose("form");
    } catch (err) {
      let errorMsg = trans(err?.data?.message ?? "Something went wrong");
      toast.error(errorMsg, {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    } finally {
      setSubmitting(false);
      handleClose("form");
    }
  };

  return (
    <>
      <Form show={teamForm}>
        <Offcanvas.Header>
          <FormHeader
            title={!isEditing ? trans("Add new team") : trans("Edit Team")}
            other={false}
            onClose={() => {
              setTeamForm(false);
              handleClose("form");
            }}
          />
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values, { setSubmitting, resetForm }) =>
              handleFormSubmit(values, setSubmitting, resetForm)
            }
            validationSchema={validationSchema}
          >
            {({ handleSubmit, isSubmitting }) => (
              <FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
                <TeamFormBody isEditing={isEditing} editData={editData} />
                <div className="offcanvas-footer m-0">
                  <div className="offcanvas-footer-body gap-8">
                    <Button
                      type={"primary"}
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? trans("Saving...") : trans("Save")}
                    </Button>
                    <Button
                      type={"default"}
                      htmlType="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setTeamForm(false);
                        handleClose("form");
                      }}
                    >
                      {trans("Cancel")}
                    </Button>
                  </div>
                </div>
              </FormFormik>
            )}
          </Formik>
        </Offcanvas.Body>
      </Form>
    </>
  );
}
